// src/components/design/PageRenderer.jsx
// Rendu d'une page bio-link : utilisé par la page publique ET par l'aperçu de l'éditeur.
import { useState, useEffect, useId } from 'react'
import {
  Lock, Music, Play, ShoppingBag,
  Calendar, HelpCircle, Timer, Star, FileText, Mail, BadgeCheck,
  AlertTriangle, ChevronRight,
} from 'lucide-react'
import api from '../../lib/axios'
import {
  getTheme, makeBgStyle, getButtonStyle, getHoverClass,
  getFont, getButtonShape, getPlatform, getLinkIcon,
  isLightColor, withAlpha, loadGoogleFonts,
  BUTTON_SIZES, TEXT_ALIGNS, CARD_SHADOWS, CARD_RADII,
  DEFAULT_SETTINGS,
} from '../../lib/pageDesign'

/* Valeurs par défaut de l'en-tête (cover / fond image) */
export const HEADER_DEFAULTS = {
  designStyle: 'minimal',
  headerLayout: 1,
  coverImage: '',
  bgImage: '',
  bgOverlay: 40,
}

/* Couleur de marque (Instagram = dégradé officiel) */
const INSTA_GRADIENT = 'linear-gradient(45deg,#F58529,#DD2A7B,#8134AF,#515BD4)'
const brandBg = (id, color) => (id === 'instagram' ? INSTA_GRADIENT : color || '#444')

const AVATAR_RADIUS = { circle: '9999px', rounded: '24px', square: '0px' }
const num = (v, d) => { const n = Number(v); return n > 0 ? n : d }

/* ============================================================
   PETITS ÉLÉMENTS DÉCORATIFS
   ============================================================ */
const Sparkle = ({ size = 11, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 0C12 7 17 12 24 12C17 12 12 17 12 24C12 17 7 12 0 12C7 12 12 7 12 0Z" fill={color} />
  </svg>
)
const Diamond = ({ size = 7, color = 'currentColor' }) => (
  <span aria-hidden="true" style={{ display: 'inline-block', width: size, height: size, background: color, transform: 'rotate(45deg)' }} />
)

/* ============================================================
   AVATAR (cercle / arche / carré / arrondi, anneau uni ou dégradé)
   ============================================================ */
function AvatarFrame({ pageInfo, settings, theme, size, rotate = 0, shape }) {
  const sh = shape || settings.avatarShape || 'circle'
  const isArch = sh === 'arch'
  const w = isArch ? Math.round(size * 0.9) : size
  const h = isArch ? Math.round(size * 1.18) : size
  const radius = isArch ? `${w / 2}px ${w / 2}px 14px 14px` : AVATAR_RADIUS[sh] || '9999px'
  const ringW = settings.avatarRing ? num(settings.avatarRingWidth, 3) : 0
  return (
    <div
      className="shrink-0"
      style={{
        width: w, height: h, borderRadius: radius, padding: ringW,
        background: settings.avatarRing || 'transparent', transform: `rotate(${rotate}deg)`,
      }}
    >
      <div className="w-full h-full overflow-hidden flex items-center justify-center"
        style={{ borderRadius: 'inherit', background: 'rgba(255,255,255,0.2)' }}>
        {pageInfo.avatar
          ? <img src={pageInfo.avatar} alt={pageInfo.title || ''} className="w-full h-full object-cover" />
          : <span className="text-2xl font-bold" style={{ color: theme.text }}>{pageInfo.title?.charAt(0)?.toUpperCase() || 'T'}</span>}
      </div>
    </div>
  )
}

/* Nom + pseudo/tagline + bio */
function NameBlock({ pageInfo, settings, theme, className = '', justify = 'center', align = 'center' }) {
  const fam = settings.nameFont ? getFont(settings.nameFont).family : undefined
  return (
    <div className={className} style={{ textAlign: align }}>
      <h1
        className={`flex items-center gap-1.5 ${settings.nameSize > 0 ? '' : 'text-2xl'} ${settings.nameUppercase ? 'uppercase tracking-[0.12em]' : ''}`}
        style={{
          color: theme.text, fontFamily: fam, justifyContent: justify,
          fontSize: settings.nameSize > 0 ? Number(settings.nameSize) : undefined,
          fontWeight: settings.nameWeight > 0 ? Number(settings.nameWeight) : 600, lineHeight: 1.15,
        }}
      >
        {pageInfo.title}
        {settings.verifiedBadge && <BadgeCheck className="w-5 h-5 fill-current shrink-0" style={{ color: settings.taglineColor || theme.accent }} />}
      </h1>
      {settings.nameUnderline && (
        <div style={{ height: 1, width: '72%', margin: '8px auto 0', background: theme.text, opacity: 0.45 }} />
      )}
      {settings.tagline && (
        <p className={`mt-1.5 text-xs ${settings.taglineUppercase ? 'uppercase tracking-[0.2em]' : ''}`}
          style={{ color: settings.taglineColor || theme.accent }}>
          {settings.tagline}
        </p>
      )}
      {pageInfo.bio && (
        <p className="text-sm opacity-80 whitespace-pre-line mt-2" style={{ color: theme.text }}>{pageInfo.bio}</p>
      )}
    </div>
  )
}

/* Layout 7 : cercle avec nom en arc au-dessus et profession en arc en dessous */
function CircleArcHeader({ pageInfo, settings, theme, size }) {
  const uid = useId().replace(/:/g, '')
  const fs = 15, gap = 12
  const rt = size / 2 + gap
  const rb = size / 2 + gap + fs
  const W = Math.round(rb * 2 + 40)
  const c = W / 2
  const fam = settings.nameFont ? getFont(settings.nameFont).family : undefined
  const top = `M ${c - rt} ${c} A ${rt} ${rt} 0 0 1 ${c + rt} ${c}`
  const bot = `M ${c - rb} ${c} A ${rb} ${rb} 0 0 0 ${c + rb} ${c}`
  return (
    <div className="flex flex-col items-center pt-4 px-6">
      <div className="relative" style={{ width: W, height: W }}>
        <div className="absolute left-0 right-0 flex items-center justify-between" style={{ top: c - 6, opacity: 0.55 }}>
          <Sparkle size={12} color={theme.text} />
          <div className="flex-1 h-px mx-1" style={{ background: theme.text, opacity: 0.6 }} />
          <div className="flex-1 h-px mx-1" style={{ background: theme.text, opacity: 0.6 }} />
          <Sparkle size={12} color={theme.text} />
        </div>
        <svg width={W} height={W} className="absolute inset-0" style={{ overflow: 'visible' }} aria-label={pageInfo.title}>
          <defs>
            <path id={`${uid}t`} d={top} />
            <path id={`${uid}b`} d={bot} />
          </defs>
          <text fill={theme.text} fontSize={fs + 3} letterSpacing="4" textAnchor="middle"
            style={{ fontFamily: fam, textTransform: 'uppercase', fontWeight: settings.nameWeight > 0 ? Number(settings.nameWeight) : 400 }}>
            <textPath href={`#${uid}t`} startOffset="50%">{pageInfo.title}</textPath>
          </text>
          {settings.tagline && (
            <text fill={settings.taglineColor || theme.text} fontSize={fs - 2} letterSpacing="3" textAnchor="middle"
              style={{ fontFamily: fam, textTransform: 'uppercase' }}>
              <textPath href={`#${uid}b`} startOffset="50%">{settings.tagline}</textPath>
            </text>
          )}
        </svg>
        <div className="absolute" style={{ left: c - size / 2, top: c - size / 2 }}>
          <AvatarFrame pageInfo={pageInfo} settings={settings} theme={theme} size={size} />
        </div>
      </div>
      {pageInfo.bio && <p className="text-sm opacity-80 whitespace-pre-line text-center -mt-2" style={{ color: theme.text }}>{pageInfo.bio}</p>}
    </div>
  )
}

/* ============================================================
   HEADER PROFIL — layouts 1-4 (historiques) + 5-10 (templates)
   ============================================================ */
function ProfileHeader({ pageInfo, settings, theme, framed }) {
  const style = settings.designStyle || 'minimal'
  const layout = Number(settings.headerLayout) || 1
  const cover = settings.coverImage
  const avatarRadius = AVATAR_RADIUS[settings.avatarShape || 'circle'] || '9999px'
  const nameProps = { pageInfo, settings, theme }

  /* ----- Layouts 5 à 10 ----- */
  if (layout === 5) {
    const img = cover || pageInfo.avatar
    const a = settings.heroAlign || 'right'
    const side = a === 'center'
      ? { left: 0, right: 0, bottom: 18, textAlign: 'center' }
      : { top: '24%', width: '52%', [a]: 0, textAlign: 'left' }
    return (
      <div className="relative" style={{ height: num(settings.headerHeight, 400) }}>
        <div className="absolute inset-0"
          style={{ background: img ? `url(${img}) center top/cover no-repeat` : withAlpha(theme.accent, '33') }} />
        <div className="absolute inset-0"
          style={{ background: `linear-gradient(to bottom, rgba(0,0,0,0) 35%, ${theme.bg} 100%)` }} />
        <div className="absolute px-6" style={side}>
          <NameBlock {...nameProps} justify={a === 'center' ? 'center' : 'flex-start'} align={a === 'center' ? 'center' : 'left'} />
        </div>
      </div>
    )
  }

  if (layout === 6) {
    return (
      <div className="flex flex-col items-center px-6 pt-10">
        <AvatarFrame pageInfo={pageInfo} settings={settings} theme={theme} size={num(settings.avatarPx, 130)} shape="arch" />
        <NameBlock {...nameProps} className="mt-4" />
      </div>
    )
  }

  if (layout === 7) {
    return <CircleArcHeader pageInfo={pageInfo} settings={settings} theme={theme} size={num(settings.avatarPx, 130)} />
  }

  if (layout === 8) {
    const size = num(settings.avatarPx, 110)
    return (
      <div className="flex flex-col items-center px-6" style={{ marginTop: framed ? -size / 2 : 16 }}>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <AvatarFrame pageInfo={pageInfo} settings={settings} theme={theme} size={size} />
        </div>
        <NameBlock {...nameProps} className="mt-4" />
      </div>
    )
  }

  if (layout === 9) {
    const size = num(settings.avatarPx, 84)
    return (
      <div>
        <div style={{ background: settings.headerBg || theme.accent }}>
          <div className="pt-6 flex justify-center relative" style={{ zIndex: 2, marginBottom: -size * 0.35 }}>
            <AvatarFrame pageInfo={pageInfo} settings={settings} theme={theme} size={size} />
          </div>
          <div style={{ height: 56, marginTop: size * 0.2, background: theme.bg, borderRadius: '50% 50% 0 0 / 100% 100% 0 0' }} />
        </div>
        <NameBlock {...nameProps} className="px-6 -mt-2" />
      </div>
    )
  }

  if (layout === 10) {
    return (
      <div className="flex flex-col items-center px-6 pt-8">
        <AvatarFrame pageInfo={pageInfo} settings={settings} theme={theme} size={num(settings.avatarPx, 104)} />
        <NameBlock {...nameProps} className="mt-4" />
      </div>
    )
  }

  /* ----- Layouts 1 à 4 (inchangés, tailles réglables) ----- */
  const S = {
    minimal:  { coverRadius: '0 0 50% 50% / 0 0 28px 28px', title: 'text-lg font-semibold', ring: 'rgba(255,255,255,0.35)' },
    creative: { coverRadius: '0 0 48px 48px', title: 'text-xl font-extrabold', ring: theme.accent },
    bold:     { coverRadius: '0', title: 'text-xl font-black uppercase tracking-wider', ring: theme.text },
  }[style] || {}

  const coverBg = cover
    ? { background: `url(${cover}) center/cover no-repeat` }
    : { background: 'rgba(255,255,255,0.18)' }

  const avatar = (size, rotate = 0) => (
    <div
      className="overflow-hidden flex items-center justify-center shrink-0"
      style={{
        width: size, height: size, borderRadius: avatarRadius,
        border: `4px solid ${S.ring}`, background: 'rgba(255,255,255,0.2)',
        transform: `rotate(${rotate}deg)`,
      }}
    >
      {pageInfo.avatar
        ? <img src={pageInfo.avatar} alt={pageInfo.title || ''} className="w-full h-full object-cover" />
        : <span className="text-2xl font-bold" style={{ color: theme.text }}>{pageInfo.title?.charAt(0)?.toUpperCase() || 'T'}</span>}
    </div>
  )

  const title = (className = '', justify = 'center') => (
    <h1 className={`${S.title} flex items-center gap-1.5 ${className}`} style={{ color: theme.text, justifyContent: justify }}>
      {pageInfo.title}
      {settings.verifiedBadge && <BadgeCheck className="w-4 h-4 fill-current" style={{ color: theme.accent }} />}
    </h1>
  )

  const bio = (className = '') =>
    pageInfo.bio
      ? <p className={`text-sm opacity-80 whitespace-pre-line ${className}`} style={{ color: theme.text }}>{pageInfo.bio}</p>
      : null

  if (layout === 1) {
    const size = num(settings.avatarPx, 80)
    return (
      <div>
        <div style={{ height: num(settings.headerHeight, 130), borderRadius: S.coverRadius, ...coverBg }} />
        <div className="flex flex-col items-center text-center px-6" style={{ marginTop: -size / 2 }}>
          {avatar(size)}
          {title('mt-3')}
          {bio('mt-1')}
        </div>
      </div>
    )
  }

  if (layout === 2) {
    const size = num(settings.avatarPx, 110)
    return (
      <div>
        <div className="relative flex justify-center" style={{ height: num(settings.headerHeight, 230), borderRadius: S.coverRadius, ...coverBg }}>
          <h1
            className={`absolute top-5 left-0 right-0 text-center text-3xl uppercase tracking-widest ${style === 'bold' ? 'font-black' : 'font-light'}`}
            style={{ color: theme.text, textShadow: '0 2px 12px rgba(0,0,0,0.35)' }}>
            {pageInfo.title}
          </h1>
          <div className="absolute" style={{ bottom: -Math.round(size * 0.44) }}>{avatar(size)}</div>
        </div>
        <div className="text-center px-6" style={{ paddingTop: Math.round(size * 0.44) + 16 }}>
          {settings.verifiedBadge && <BadgeCheck className="w-4 h-4 fill-current inline mb-1" style={{ color: theme.accent }} />}
          {bio()}
        </div>
      </div>
    )
  }

  if (layout === 3) {
    const size = num(settings.avatarPx, 110)
    return (
      <div className="px-6 pt-8 text-center">
        {title()}
        <div className="relative mx-auto mt-5" style={{ width: size + 90, height: size + 40 }}>
          <div className="absolute left-0 top-2">{avatar(size, -8)}</div>
          {cover && (
            <div className="absolute right-0 top-8 overflow-hidden"
              style={{
                width: size, height: size,
                borderRadius: avatarRadius === '9999px' ? '24px' : avatarRadius,
                border: `4px solid ${S.ring}`, transform: 'rotate(8deg)', ...coverBg,
              }} />
          )}
        </div>
        {bio('mt-2')}
      </div>
    )
  }

  const size4 = num(settings.avatarPx, 64)
  return (
    <div>
      <div style={{ height: num(settings.headerHeight, 110), borderRadius: S.coverRadius, ...coverBg }} />
      <div className="px-6 text-left" style={{ marginTop: -size4 / 2 }}>
        {avatar(size4)}
        {title('mt-2', 'flex-start')}
        {bio('mt-1')}
      </div>
    </div>
  )
}

/* ============================================================
   RÉSEAUX : rangée d'icônes (avec traits + losanges optionnels)
   ============================================================ */
function SocialRow({ blocks, settings, theme, surface, onLinkClick }) {
  const st = settings.socialStyle || 'round-solid'
  const bg = settings.socialBg || theme.text
  const fg = settings.socialFg || theme.bg
  const itemStyle = {
    'round-solid': { background: bg, color: fg, borderRadius: '9999px' },
    soft: { background: surface, color: theme.text, borderRadius: '9999px' },
    square: { background: 'rgba(0,0,0,0.35)', color: theme.text, border: `1px solid ${withAlpha(theme.accent, '88')}`, borderRadius: 10 },
    plain: { color: theme.text },
  }[st] || {}

  const icons = (
    <div className="flex flex-wrap items-center justify-center gap-3">
      {blocks.map((b) => {
        const c = b.content || {}
        const P = getPlatform(c.platform)
        const Icon = P.icon
        const st2 = st === 'brand'
          ? { background: brandBg(c.platform, P.color), color: '#fff', borderRadius: settings.socialRadius || '9999px' }
          : itemStyle
        return (
          <a key={b.id} href={c.url || '#'} target="_blank" rel="noreferrer" aria-label={P.label}
            onClick={() => onLinkClick(b)}
            className="flex items-center justify-center transition hover:scale-105"
            style={{ width: 40, height: 40, ...st2 }}>
            <Icon className="w-[18px] h-[18px]" />
          </a>
        )
      })}
    </div>
  )

  if (!settings.socialLines) return <div className="py-1">{icons}</div>

  const line = (left) => (
    <div className="flex-1 flex items-center" style={{ flexDirection: left ? 'row' : 'row-reverse' }}>
      <div className="flex-1 h-px"
        style={{ background: `linear-gradient(${left ? '90deg' : '270deg'}, transparent, ${theme.accent})` }} />
      <Diamond color={theme.accent} />
    </div>
  )
  return <div className="flex items-center gap-3 py-1">{line(true)}{icons}{line(false)}</div>
}

/* Titre manuscrit ("Sua melhor versão…", "let's be friends!") */
function Headline({ settings, theme }) {
  if (!settings.headline) return null
  return (
    <p className="text-center py-1"
      style={{
        fontFamily: getFont(settings.headlineFont || settings.font).family,
        color: settings.headlineColor || theme.accent, fontSize: 26, lineHeight: 1.25,
      }}>
      {settings.headline}
    </p>
  )
}

/* ============================================================
   LIENS : bouton / orné / carte / grande carte ronde
   ============================================================ */
function hostOf(url) {
  try { return new URL(url).hostname.replace(/^www\./, '') } catch { return '' }
}

function LinkItem({ block, ctx }) {
  const { settings, theme, linkColors, shape, btnSize, hoverCls, cardShadow, onLinkClick, isFirstLink, linkIndex } = ctx
  const c = block.content || {}
  const layout = settings.linkLayout || 'button'
  const hl = isFirstLink && settings.highlightFirst

  const bg = hl && settings.linkHighlightBg ? settings.linkHighlightBg : linkColors?.bg
  const text = (hl && settings.linkHighlightText ? settings.linkHighlightText : linkColors?.text) || theme.text
  const base = linkColors
    ? { background: bg, color: text, border: linkColors.border ? `1px solid ${linkColors.border}` : 'none' }
    : ctx.btnStyle

  const h = Number(settings.linkHeight) || 0
  const w = Math.min(100, Math.max(40, Number(settings.linkWidth) || 100))
  const radius = layout === 'card-round' ? Math.max(parseInt(shape.radius, 10) || 0, 28) : shape.radius
  const style = { ...base, borderRadius: radius, boxShadow: cardShadow, width: `${w}%`, minHeight: h || undefined }

  const title = c.title || 'Lien'
  const mode = settings.linkShowSubtitle || 'none'
  const sub = mode === 'text' ? c.subtitle : mode === 'url' ? (c.subtitle || hostOf(c.url)) : ''
  const { Icon, color } = getLinkIcon(c.icon)
  const common = { href: c.url || '#', target: '_blank', rel: 'noreferrer', onClick: () => onLinkClick(block) }
  const upper = settings.linkUppercase

  if (layout === 'card' || layout === 'card-round') {
    const ist = settings.linkIconStyle
    let tile = null
    if (ist === 'brand') {
      tile = <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: color, color: '#fff' }}><Icon className="w-5 h-5" /></div>
    } else if (ist === 'ring') {
      tile = (
        <div className="w-[60px] h-[60px] rounded-full flex items-center justify-center shrink-0"
          style={{
            border: `2px solid ${withAlpha(theme.accent, '88')}`, color: text,
            background: 'radial-gradient(circle at 30% 25%, rgba(255,255,255,.65), rgba(255,255,255,0) 70%)',
            boxShadow: '0 4px 10px rgba(0,0,0,.12)',
          }}>
          <Icon className="w-6 h-6" style={{ opacity: 0.8 }} />
        </div>
      )
    } else if (ist !== 'none') {
      tile = <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(255,255,255,.85)', color: text }}><Icon className="w-[18px] h-[18px]" /></div>
    }
    const round = layout === 'card-round'
    return (
      <a {...common} className={`flex items-center gap-3 px-3 py-2 text-left transition-all ${hoverCls}`} style={{ ...style, margin: '0 auto' }}>
        {tile}
        <div className="flex-1 min-w-0">
          <p className={round ? 'text-[17px] tracking-wide' : 'text-sm font-semibold truncate'}
            style={{ textTransform: round || upper ? 'uppercase' : undefined, fontFamily: round ? getFont(settings.nameFont || settings.font).family : undefined }}>
            {title}
          </p>
          {sub && <p className={`text-xs opacity-75 leading-snug ${round ? '' : 'truncate'}`}>{sub}</p>}
        </div>
        {round
          ? <span className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ border: `1px solid ${withAlpha(text, '66')}` }}><ChevronRight className="w-4 h-4" /></span>
          : <ChevronRight className="w-4 h-4 opacity-60 shrink-0" />}
      </a>
    )
  }

  // bouton / bouton orné
  const orn = layout === 'ornate' ? settings.ornament : 'none'
  const side = linkIndex % 2 === 0 ? 'left' : 'right'
  return (
    <div className="relative flex justify-center">
      <a {...common} className={`flex items-center justify-center gap-2 transition-all ${btnSize} ${hoverCls}`}
        style={{ ...style, textTransform: upper ? 'uppercase' : undefined, letterSpacing: upper ? '.08em' : undefined }}>
        <span>{title}</span>
      </a>
      {orn && orn !== 'none' && (
        <span className="absolute top-1/2 -translate-y-1/2" style={{ [side]: `calc(${(100 - w) / 2}% - 15px)`, color: theme.text }}>
          {orn === 'sparkle' ? <Sparkle size={11} /> : <Diamond size={7} />}
        </span>
      )}
    </div>
  )
}

/* ============================================================
   BLOCS
   ============================================================ */
function CountdownTimer({ target }) {
  const [left, setLeft] = useState(() => Math.max(0, new Date(target).getTime() - Date.now()))
  useEffect(() => {
    const t = setInterval(() => setLeft(Math.max(0, new Date(target).getTime() - Date.now())), 1000)
    return () => clearInterval(t)
  }, [target])
  const d = Math.floor(left / 86400000), h = Math.floor((left % 86400000) / 3600000)
  const m = Math.floor((left % 3600000) / 60000), s = Math.floor((left % 60000) / 1000)
  return (
    <div className="flex justify-center gap-2 text-center">
      {[[d, 'j'], [h, 'h'], [m, 'm'], [s, 's']].map(([v, l]) => (
        <div key={l} className="bg-white/15 rounded-lg px-3 py-2 min-w-[52px]">
          <div className="text-lg font-bold">{String(v).padStart(2, '0')}</div>
          <div className="text-[10px] opacity-70">{l}</div>
        </div>
      ))}
    </div>
  )
}

function EmailCaptureBlock({ c, baseBtn, btnSize, cardStyle, slug, surface }) {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState('idle') // idle | sending | done | error
  const submit = async (e) => {
    e.preventDefault()
    if (!email) return
    setStatus('sending')
    try {
      await api.post(`/pages/${slug}/subscribe`, { email })
      setStatus('done')
    } catch { setStatus('error') }
  }
  return (
    <div className="p-4 space-y-2 text-left" style={cardStyle}>
      <h3 className="font-bold text-sm flex items-center gap-1.5"><Mail className="w-4 h-4" />{c.title || 'Newsletter'}</h3>
      {c.subtitle && <p className="text-xs opacity-70">{c.subtitle}</p>}
      {status === 'done' ? (
        <p className="text-xs font-semibold py-2">Merci, c'est enregistré ✅</p>
      ) : (
        <form onSubmit={submit} className="space-y-2">
          <input required type="email" value={email} onChange={e => setEmail(e.target.value)}
            placeholder="ton@email.com" className="w-full px-3 py-2 rounded-lg text-xs placeholder:opacity-60 outline-none"
            style={{ background: surface }} />
          <button type="submit" disabled={status === 'sending'} className={`w-full rounded-lg text-xs font-semibold ${btnSize}`} style={baseBtn}>
            {status === 'sending' ? '...' : (c.buttonLabel || "S'inscrire")}
          </button>
          {status === 'error' && <p className="text-[11px] text-red-400">Erreur, réessaie.</p>}
        </form>
      )}
    </div>
  )
}

function ContactBlock({ c, baseBtn, btnSize, cardStyle, slug, surface }) {
  const [form, setForm] = useState({ name: '', email: '', message: '' })
  const [status, setStatus] = useState('idle')
  const submit = async (e) => {
    e.preventDefault()
    setStatus('sending')
    try {
      await api.post(`/pages/${slug}/contact`, form)
      setStatus('done')
    } catch { setStatus('error') }
  }
  const field = 'w-full px-3 py-2 rounded-lg text-xs placeholder:opacity-60 outline-none'
  if (status === 'done') return (
    <div className="p-4 text-left" style={cardStyle}><p className="text-sm font-semibold">Message envoyé ✅</p></div>
  )
  return (
    <form onSubmit={submit} className="p-4 space-y-2 text-left" style={cardStyle}>
      <h3 className="font-bold text-sm">{c.title || 'Contact'}</h3>
      {c.subtitle && <p className="text-xs opacity-70">{c.subtitle}</p>}
      <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Votre nom" className={field} style={{ background: surface }} />
      <input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Votre email" className={field} style={{ background: surface }} />
      <textarea rows={3} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder="Message" className={`${field} resize-none`} style={{ background: surface }} />
      <button type="submit" disabled={status === 'sending'} className={`w-full rounded-lg text-xs font-semibold ${btnSize}`} style={baseBtn}>Envoyer</button>
      {status === 'error' && <p className="text-[11px] text-red-400">Erreur, réessaie.</p>}
    </form>
  )
}

function PublicBlock({ block, ctx }) {
  const { shape, btnSize, btnStyle, hoverCls, cardShadow, cardRadius, slug, onLinkClick, surface, settings } = ctx
  const c = block.content || {}
  const baseBtn = { ...btnStyle, borderRadius: shape.radius, boxShadow: cardShadow }
  const cardStyle = { background: surface, borderRadius: cardRadius, boxShadow: cardShadow }

  switch (block.type) {
    case 'header':
      return <div className="py-2"><h2 className="text-xl font-bold">{c.title}</h2>{c.subtitle && <p className="text-sm opacity-70">{c.subtitle}</p>}</div>

    case 'link':
      return <LinkItem block={block} ctx={ctx} />

    case 'image': {
      if (!c.url) return null
      const img = <img src={c.url} alt={c.caption || ''} className="w-full" style={{ borderRadius: cardRadius }} loading="lazy" />
      return (
        <figure>
          {c.linkUrl ? <a href={c.linkUrl} target="_blank" rel="noreferrer" onClick={() => onLinkClick(block)}>{img}</a> : img}
          {c.caption && <figcaption className="text-xs opacity-70 mt-1">{c.caption}</figcaption>}
        </figure>
      )
    }

    case 'video': {
      if (!c.url) return null
      const embed = toEmbeddableVideoUrl(c.url)
      return (
        <div className="aspect-video overflow-hidden" style={{ borderRadius: cardRadius }}>
          {embed
            ? <iframe title="video" src={embed} className="w-full h-full" allowFullScreen loading="lazy" />
            : <a href={c.url} target="_blank" rel="noreferrer" className="w-full h-full bg-black/30 flex items-center justify-center"><Play className="w-10 h-10 opacity-70" /></a>}
        </div>
      )
    }

    case 'music':
      return c.url ? (
        <a href={c.url} target="_blank" rel="noreferrer" onClick={() => onLinkClick(block)} className="p-3 flex items-center gap-3" style={cardStyle}>
          <Music className="w-5 h-5" /><span className="text-sm truncate">Écouter sur {getPlatform(c.platform).label}</span>
        </a>
      ) : null

    case 'social': {
      const plat = getPlatform(c.platform)
      const PIcon = plat.icon
      const useBrand = settings.socialStyle === 'brand'
      const st = useBrand
        ? { ...baseBtn, background: brandBg(c.platform, plat.color), color: '#fff', border: 'none' }
        : baseBtn
      return (
        <a href={c.url || '#'} target="_blank" rel="noreferrer" onClick={() => onLinkClick(block)}
          className={`flex items-center justify-center gap-2 w-full ${btnSize} ${hoverCls}`} style={st}>
          <PIcon className="w-4 h-4" style={useBrand ? undefined : { color: plat.color }} /><span>{plat.label}</span>
        </a>
      )
    }

    case 'map':
      return c.address ? (
        <div className="overflow-hidden" style={{ borderRadius: cardRadius, height: 160 }}>
          <iframe title="map" width="100%" height="100%" style={{ border: 0 }} loading="lazy"
            src={`https://www.google.com/maps?q=${encodeURIComponent(c.address)}&output=embed`} />
        </div>
      ) : null

    case 'contact':
      return <ContactBlock c={c} baseBtn={baseBtn} btnSize={btnSize} cardStyle={cardStyle} slug={slug} surface={surface} />

    case 'email_capture':
      return <EmailCaptureBlock c={c} baseBtn={baseBtn} btnSize={btnSize} cardStyle={cardStyle} slug={slug} surface={surface} />

    case 'shop':
      return (
        <div className="overflow-hidden text-left" style={cardStyle}>
          {c.image && <img src={c.image} alt="" className="w-full h-36 object-cover" loading="lazy" />}
          <div className="p-3 space-y-1">
            <p className="font-semibold text-sm">{c.name || 'Produit'}</p>
            <p className="text-xs opacity-70">{c.price ? `${c.price} ${c.currency || 'EUR'}` : ''}</p>
            <a href={c.checkoutUrl || '#'} target="_blank" rel="noreferrer" onClick={() => onLinkClick(block)}
              className={`w-full mt-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 ${btnSize}`} style={baseBtn}>
              <ShoppingBag className="w-3.5 h-3.5" /> Acheter
            </a>
          </div>
        </div>
      )

    case 'booking':
      return (
        <a href={c.calendarUrl || '#'} target="_blank" rel="noreferrer" onClick={() => onLinkClick(block)} className="block p-4 text-left" style={cardStyle}>
          <p className="font-semibold text-sm flex items-center gap-1.5"><Calendar className="w-4 h-4" />{c.title || 'Réserver un créneau'}</p>
          {c.subtitle && <p className="text-xs opacity-70 mt-0.5">{c.subtitle}</p>}
        </a>
      )

    case 'faq':
      return (
        <div className="text-left space-y-2">
          {(c.items || []).map((item, i) => (
            <details key={i} className="p-3" style={cardStyle}>
              <summary className="text-sm font-semibold cursor-pointer flex items-center gap-1.5"><HelpCircle className="w-4 h-4 shrink-0" />{item.q || 'Question'}</summary>
              <p className="text-xs opacity-70 mt-2">{item.a}</p>
            </details>
          ))}
        </div>
      )

    case 'countdown':
      return (
        <div className="p-4 space-y-2" style={cardStyle}>
          {c.title && <p className="text-sm font-semibold flex items-center justify-center gap-1.5"><Timer className="w-4 h-4" />{c.title}</p>}
          {c.targetDate && <CountdownTimer target={c.targetDate} />}
          {c.buttonUrl && <a href={c.buttonUrl} onClick={() => onLinkClick(block)} className={`block text-center mt-2 rounded-lg text-xs font-semibold ${btnSize}`} style={baseBtn}>En profiter</a>}
        </div>
      )

    case 'review':
      return (
        <div className="p-4 text-left space-y-1" style={cardStyle}>
          <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className="w-3.5 h-3.5" fill={i < (c.rating || 5) ? 'currentColor' : 'none'} />)}</div>
          <p className="text-xs italic opacity-90">"{c.text}"</p>
          {c.author && <p className="text-[11px] font-semibold opacity-70">— {c.author}</p>}
        </div>
      )

    case 'embed':
      return c.embedUrl ? (
        <div className="overflow-hidden" style={{ borderRadius: cardRadius, height: 240 }}>
          <iframe title="embed" src={c.embedUrl} width="100%" height="100%" style={{ border: 0 }} loading="lazy" />
        </div>
      ) : null

    case 'pdf':
      return c.url ? (
        <a href={c.url} target="_blank" rel="noreferrer" onClick={() => onLinkClick(block)} className="flex items-center gap-2 p-3" style={cardStyle}>
          <FileText className="w-4 h-4" /><span className="text-sm">{c.label || 'Document PDF'}</span>
        </a>
      ) : null

    case 'text_divider':
      return <div className="flex items-center gap-3 py-1"><div className="flex-1 h-px bg-current opacity-20" /><span className="text-xs opacity-60">{c.label}</span><div className="flex-1 h-px bg-current opacity-20" /></div>

    case 'separator':
      return <div style={{ height: { sm: '8px', md: '16px', lg: '32px' }[c.size] || '16px' }} />

    default:
      return null
  }
}

function toEmbeddableVideoUrl(url) {
  try {
    const u = new URL(url)
    if (u.hostname.includes('youtube.com')) {
      const id = u.searchParams.get('v'); return id ? `https://www.youtube.com/embed/${id}` : null
    }
    if (u.hostname === 'youtu.be') return `https://www.youtube.com/embed/${u.pathname.slice(1)}`
    if (u.hostname.includes('vimeo.com')) return `https://player.vimeo.com/video/${u.pathname.split('/').pop()}`
    return null
  } catch { return null }
}

/* Regroupe les réseaux en rangée d'icônes, place le titre manuscrit */
function buildItems(blocks, settings) {
  const icons = settings.socialLayout === 'icons'
  let items = []
  let li = 0
  blocks.forEach((b) => {
    if (icons && b.type === 'social') {
      const last = items[items.length - 1]
      if (last && last.kind === 'socials') last.blocks.push(b)
      else items.push({ kind: 'socials', blocks: [b], key: `soc-${b.id}` })
    } else {
      items.push({ kind: 'block', block: b, linkIndex: b.type === 'link' ? li++ : -1 })
    }
  })

  if (icons && settings.socialPosition === 'top') {
    const soc = items.filter((i) => i.kind === 'socials')
    if (soc.length) {
      items = [{ kind: 'socials', blocks: soc.flatMap((s) => s.blocks), key: 'soc-top' }, ...items.filter((i) => i.kind !== 'socials')]
    }
  }

  if (settings.headline) {
    const head = { kind: 'headline', key: 'headline' }
    if (settings.headlinePos === 'below-links') {
      const last = items.length - 1
      if (last >= 0 && items[last].kind === 'socials') items.splice(last, 0, head)
      else items.push(head)
    } else {
      const at = items[0]?.kind === 'socials' && settings.socialPosition === 'top' ? 1 : 0
      items.splice(at, 0, head)
    }
  }
  return items
}

/* ============================================================
   RENDU PARTAGÉ — page publique ET aperçu de l'éditeur
   preview=true : pas de mot de passe, pas de tracking, liens/formulaires inertes
   ============================================================ */
export function PageRenderer({ page, blocks = [], slug = '', preview = false, onLinkClick = () => {} }) {
  const settings = { ...DEFAULT_SETTINGS, ...HEADER_DEFAULTS, ...(page.settings || {}) }
  const [unlocked, setUnlocked] = useState(preview || !settings.passwordProtected)
  const [passwordInput, setPasswordInput] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [sensitiveAccepted, setSensitiveAccepted] = useState(preview)

  // Polices Google du template (corps, nom, titre manuscrit)
  useEffect(() => {
    loadGoogleFonts([settings.font, settings.nameFont, settings.headlineFont])
  }, [settings.font, settings.nameFont, settings.headlineFont])

  // Vue trackée une fois la page déverrouillée et affichée
  useEffect(() => {
    if (!preview && unlocked && slug) {
      api.post(`/public/pages/${slug}/view`).catch(() => {})
    }
  }, [preview, unlocked, slug])

  const handleUnlock = (e) => {
    e.preventDefault()
    if (passwordInput === settings.pagePassword) { setUnlocked(true); setPasswordError('') }
    else setPasswordError('Mot de passe incorrect')
  }

  const theme = getTheme(settings)
  const font = getFont(settings.font)

  // Fond : image + overlay sombre si présente, sinon fond du thème (+ halos éventuels)
  const overlay = (settings.bgOverlay ?? 40) / 100
  const bg = settings.bgImage
    ? {
        backgroundImage: `linear-gradient(rgba(0,0,0,${overlay}), rgba(0,0,0,${overlay})), url(${settings.bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: preview ? 'scroll' : 'fixed',
      }
    : makeBgStyle(theme, settings)

  const shape = getButtonShape(settings.buttonShape)
  const align = TEXT_ALIGNS.find(a => a.id === settings.textAlign)?.cls || 'text-center'
  const cardShadow = CARD_SHADOWS[settings.shadow] || ''
  const btnSize = BUTTON_SIZES[settings.buttonSize] || BUTTON_SIZES.md
  const hoverCls = getHoverClass(settings.buttonHover)
  const cardRadius = CARD_RADII[settings.borderRadius] || '16px'

  // Couleurs de liens personnalisées (template) sinon style historique
  const linkColors = settings.linkBg
    ? { bg: settings.linkBg, text: settings.linkText || theme.text, border: settings.linkBorder || '' }
    : null
  const btnStyle = linkColors
    ? { background: linkColors.bg, color: linkColors.text, border: linkColors.border ? `1px solid ${linkColors.border}` : 'none' }
    : getButtonStyle(theme, settings.buttonStyle)
  const surface = settings.surface || (isLightColor(theme.bg) ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)')

  const now = new Date()
  const visibleBlocks = blocks.filter(b => {
    if (preview) return true
    if (b.type !== 'link' || !b.content?.scheduled) return true
    if (b.content.startAt && now < new Date(b.content.startAt)) return false
    if (b.content.endAt && now > new Date(b.content.endAt)) return false
    return true
  })
  const items = buildItems(visibleBlocks, settings)

  const baseCtx = {
    settings, theme, shape, btnSize, btnStyle, linkColors, hoverCls, cardShadow, cardRadius,
    slug, onLinkClick, surface,
  }

  // Cadre autour de la page (fin ou en arche)
  const layout = Number(settings.headerLayout) || 1
  const frame = settings.pageFrame
  const framed = frame === 'thin' || frame === 'arch'
  const frameStyle = framed
    ? {
        border: `1px solid ${withAlpha(theme.text, '88')}`,
        borderRadius: frame === 'arch' ? '9999px 9999px 0 0' : 0,
        margin: `${layout === 8 ? num(settings.avatarPx, 110) / 2 + 16 : 16}px 16px 0`,
        paddingBottom: 28,
        overflow: layout === 8 ? 'visible' : 'hidden',
      }
    : undefined

  // Aperçu : on neutralise clics et envois de formulaires
  const inert = preview
    ? { onClickCapture: (e) => { if (e.target.closest('a')) e.preventDefault() }, onSubmitCapture: (e) => e.preventDefault() }
    : {}

  return (
    <div className={`${preview ? 'min-h-full' : 'min-h-screen'} w-full`}
      style={{ ...bg, fontFamily: font.family, color: theme.text }} {...inert}>
      {settings.passwordProtected && !unlocked ? (
        <div className="min-h-screen flex items-center justify-center px-6">
          <form onSubmit={handleUnlock} className="w-full max-w-xs text-center space-y-3">
            <Lock className="w-8 h-8 mx-auto opacity-80" />
            <h1 className="text-lg font-bold">{page.title}</h1>
            <p className="text-xs opacity-70">Cette page est protégée par un mot de passe.</p>
            <input type="password" value={passwordInput} onChange={e => setPasswordInput(e.target.value)}
              placeholder="Mot de passe" className="w-full px-3 py-2.5 rounded-lg text-sm text-center outline-none placeholder:opacity-60"
              style={{ background: surface }} />
            {passwordError && <p className="text-xs text-red-400">{passwordError}</p>}
            <button type="submit" className="w-full py-2.5 rounded-lg text-sm font-semibold" style={{ background: theme.accent, color: theme.text }}>Déverrouiller</button>
          </form>
        </div>
      ) : settings.sensitiveContentWarning && !sensitiveAccepted ? (
        <div className="min-h-screen flex items-center justify-center px-6">
          <div className="w-full max-w-xs text-center space-y-3">
            <AlertTriangle className="w-8 h-8 mx-auto opacity-80" />
            <p className="text-sm opacity-90">Cette page peut contenir du contenu sensible.</p>
            <button onClick={() => setSensitiveAccepted(true)} className="w-full py-2.5 rounded-lg text-sm font-semibold" style={{ background: theme.accent, color: theme.text }}>Continuer</button>
          </div>
        </div>
      ) : (
        <div className={`mx-auto pb-10 ${align}`} style={{ maxWidth: num(settings.contentWidth, 576) }}>
          <div style={frameStyle}>
            <ProfileHeader
              pageInfo={{ title: page.title, bio: page.bio, avatar: page.avatar }}
              settings={settings}
              theme={theme}
              framed={framed}
            />

            <div className="px-6 mt-6 flex flex-col" style={{ gap: num(settings.linkGap, 12) }}>
              {items.map((it) => {
                if (it.kind === 'headline') return <Headline key={it.key} settings={settings} theme={theme} />
                if (it.kind === 'socials') {
                  return <SocialRow key={it.key} blocks={it.blocks} settings={settings} theme={theme} surface={surface} onLinkClick={onLinkClick} />
                }
                return (
                  <PublicBlock key={it.block.id} block={it.block}
                    ctx={{ ...baseCtx, linkIndex: it.linkIndex, isFirstLink: it.linkIndex === 0 }} />
                )
              })}
            </div>
          </div>

          <div className="px-6 pt-8 space-y-1">
            {settings.footerText && <p className="text-[11px] opacity-70">{settings.footerText}</p>}
            <a href="https://tapio.app" target="_blank" rel="noreferrer" className="block text-[10px] opacity-50">Propulsé par Tapio</a>
          </div>
        </div>
      )}
    </div>
  )
}

export default PageRenderer